import { Link } from 'react-router-dom';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';

const LOGO_URL = '/logo.svg';

export default function Footer() {
  const { businessName, businessPhone, businessEmail, businessAddress, businessHours } = useBusinessSettings();

  const navLinks = [
    { to: '/sales', label: 'Homes for Sale' },
    { to: '/sellers', label: 'For Sellers' },
    { to: '/buyers', label: 'For Buyers' },
    { to: '/team', label: 'Our Team' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <footer className="bg-dark-900 text-white rounded-2xl mx-4 mb-4 mt-16 overflow-hidden">
      <div className="px-6 py-12 md:px-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* CTA Column */}
          <div className="lg:col-span-1">
            <h3 className="font-display font-bold text-2xl md:text-3xl leading-tight mb-6">
              Sell for more.
              <br />
              Buy with strategy.
            </h3>
            <p className="text-dark-300 text-sm leading-relaxed">
              A results-driven real estate brokerage with instant AI home valuation and AI renovation visualization.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-medium text-white mb-5 text-sm uppercase tracking-wider">
              Navigation
            </h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-dark-300 hover:text-gold-400 text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-medium text-white mb-5 text-sm uppercase tracking-wider">
              Contact
            </h4>
            <ul className="space-y-3">
              {businessPhone && (
                <li>
                  <a href={`tel:${businessPhone.replace(/[^+\d]/g, '')}`} className="flex items-center gap-2 text-dark-300 hover:text-white text-sm transition-colors">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-phone-line" />
                    </span>
                    {businessPhone}
                  </a>
                </li>
              )}
              {businessEmail && (
                <li>
                  <a href={`mailto:${businessEmail}`} className="flex items-center gap-2 text-dark-300 hover:text-white text-sm transition-colors">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-mail-line" />
                    </span>
                    {businessEmail}
                  </a>
                </li>
              )}
              {businessAddress && (
                <li className="flex items-start gap-2 text-dark-300 text-sm">
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-line" />
                  </span>
                  {businessAddress}
                </li>
              )}
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-medium text-white mb-5 text-sm uppercase tracking-wider">
              Business Hours
            </h4>
            {businessHours.length > 0 ? (
              <ul className="space-y-2">
                {businessHours.map((h) => (
                  <li key={h.day} className="flex justify-between text-sm">
                    <span className="text-dark-400">{h.day.slice(0, 3)}</span>
                    <span className={h.closed ? 'text-red-400' : 'text-dark-300'}>
                      {h.closed ? 'Closed' : `${h.open} - ${h.close}`}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="space-y-2">
                <li className="flex justify-between text-sm">
                  <span className="text-dark-400">Mon-Fri</span>
                  <span className="text-dark-300">9:00 AM - 6:00 PM</span>
                </li>
                <li className="flex justify-between text-sm">
                  <span className="text-dark-400">Sat</span>
                  <span className="text-dark-300">10:00 AM - 2:00 PM</span>
                </li>
                <li className="flex justify-between text-sm">
                  <span className="text-dark-400">Sun</span>
                  <span className="text-red-400">Closed</span>
                </li>
              </ul>
            )}
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-medium text-white mb-2 text-sm">
              Stay Updated
            </h4>
            <p className="text-dark-400 text-sm mb-4">
              Get the latest listings and market insights delivered to your inbox.
            </p>
            <form
              data-readdy-form
              action="https://readdy.ai/api/form/d8ab06k09nijib7vm0rg"
              method="POST"
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                fetch(form.action, {
                  method: 'POST',
                  body: new URLSearchParams(formData as any),
                  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                }).catch(() => {});
              }}
            >
              <input
                type="email"
                name="email"
                required
                placeholder="Enter your email"
                className="flex-1 bg-dark-800 border border-dark-700 text-white text-sm px-4 py-2.5 rounded-full placeholder:text-dark-500 focus:outline-none focus:border-gold-500"
              />
              <button
                type="submit"
                className="w-10 h-10 bg-gold-500 rounded-full flex items-center justify-center hover:bg-gold-600 transition-colors flex-shrink-0"
              >
                <i className="ri-arrow-right-line text-white" />
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Brand Decoration */}
        <div className="mt-12 pt-8 border-t border-dark-800">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <img
                src={LOGO_URL}
                alt={businessName}
                className="w-8 h-8 object-contain"
              />
              <span className="font-display font-bold text-lg">{businessName}</span>
            </div>
            <p className="text-dark-500 text-xs">
              &copy; 2026 {businessName}. All rights reserved.
            </p>
          </div>

          {/* Developer Credit */}
          <div className="mt-4 pt-4 border-t border-dark-800/50 flex items-center justify-center gap-2">
            <span className="text-dark-500 text-xs">Developed by</span>
            <img
              src="https://storage.readdy-site.link/project_files/f67b4dff-6548-4eef-b7b7-18e962492894/6d71855a-8cb8-4293-900f-d44569a7024b_compressed_ebd0e821-dee9-47d6-99f1-4f2f4a9250a2.webp"
              alt="CodeLab"
              className="h-6 w-auto object-contain"
            />
          </div>
        </div>
      </div>
    </footer>
  );
}